import { createFileRoute } from "@tanstack/react-router";
import { getFileStorageBaseURL } from "common/constants/fileStorageBaseUrl";
import Flags from "common/constants/flags";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import {
  matchesMaterialCacheKeyFormat,
  type MaterialSrc,
  materialSrcEquals,
  materialSrcFromCacheKey,
} from "common/materials/materialSrc";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { initSharedWebGL, WebGLContextReuse } from "gfx/activeSharedWebGL";
import {
  currentMaterialBakingCacheId,
  getMaterialBakingAwsPath,
  getMaterialBakingAwsRoot,
  materialBakingCacheDevSuffix,
} from "gfx/designMaterialBakedTextureCache";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { designMaterialNewFromPreprocessed } from "gfx/materials/designMaterial";
import { designMaterialBakeColorMapToBlob } from "gfx/materials/designMaterialBakeColorMap";
import {
  designMaterialBakesToFlatColor,
  designMaterialColorVariantsCanUseTemplateTexture,
} from "gfx/materials/designMaterialBakeColorMapUtils";
import { preprocessMaterialParams } from "gfx/materials/designMaterialParams";
import { defaultDesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import { logMaterialSrc } from "gfx/materials/logMaterialSrc";
import { usePreloadedMaterialTextures } from "gfx/materials/usePreloadedMaterialTextures";
import { uniqueMaterialsInBuiltInPalettes } from "lib/house/builtInPaletteMaterialsList";
import { materialAppearanceTestDefaultMaterials } from "lib/house/materialLists";
import { postFile, listFiles } from "lib/storage/fileStorage";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import type { WebGLRenderer } from "three";
import DebugLogViewer, { useDebugLogViewer } from "ui/components/DebugLogViewer";
import Checkbox from "ui/forms/CheckboxOld";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";

let fullMaterialSrcListCached: MaterialSrc[] = [];

function fullMaterialSrcList(): MaterialSrc[] {
  if (fullMaterialSrcListCached.length === 0) {
    const fullSet = new Set<MaterialSrc>(
      uniqueMaterialsInBuiltInPalettes.map((materialCacheKey) => materialSrcFromCacheKey(materialCacheKey)),
    );

    // Include materials used on test pages.
    for (const material of materialAppearanceTestDefaultMaterials) {
      fullSet.add(material);
    }
    fullMaterialSrcListCached = Array.from(fullSet);
  }
  return fullMaterialSrcListCached;
}

enum MaterialSrcList {
  All = "All",
  TestMaterials = "TestMaterials",
}

interface BakingError {
  materialId: MaterialSrc;
  error: string;
}

function MaterialCacheManager() {
  const [materialSrcList, setMaterialSrcList] = useState<MaterialSrcList>(MaterialSrcList.All);
  const [retries, setRetries] = useState<number>(1);
  const [isBaking, setIsBaking] = useState<boolean>(false);
  const [finishedImageCount, setFinishedImageCount] = useState<number>(0);
  const [currentBakeTotalCount, setCurrentBakeTotalCount] = useState<number>(0);
  const [erroredMaterialIds, setErroredMaterialIds] = useState<MaterialSrc[]>([]);
  const { loadTextureUrl, texturesReady } = usePreloadedMaterialTextures();
  const [fileStorageBaseURL, setFileStorageBaseURL] = useState<string | null>(null);
  const [useDevCache, setUseDevCache] = useState<boolean>(Flags.IS_DEV_MODE);
  const [cacheId, setCacheId] = useState<string>("");
  const [refreshAllWhenBaking, setRefreshAllWhenBaking] = useState<boolean>(false);
  const [filterList, setFilterList] = useState<string>("");
  const [materialIdsToBake, setMaterialIdsToBake] = useState<MaterialSrc[]>([]);
  const { addLogMessage, addErrorMessage, logElementRef, logMessages } = useDebugLogViewer();

  useEffect(() => {
    void getFileStorageBaseURL().then((url) => {
      if (url) {
        setFileStorageBaseURL(url);
      }
    });
  }, []);

  useEffect(() => {
    setCacheId(currentMaterialBakingCacheId + (useDevCache ? materialBakingCacheDevSuffix : ""));
  }, [useDevCache]);

  useEffect(() => {
    let list: MaterialSrc[] =
      materialSrcList === MaterialSrcList.All ? fullMaterialSrcList() : materialAppearanceTestDefaultMaterials;
    if (filterList.trim().length > 0) {
      const filterTrimmedNames = filterList.split("\n").map((name) => name.trim().toLowerCase());
      const templateIdsFromFilter: number[] = [];
      const materialSrcFromFilter: MaterialSrc[] = [];
      for (const template of Object.entries(TemplateIdToName)) {
        const templateNameLower = template[1].toLowerCase();
        for (const trimmedName of filterTrimmedNames) {
          if (templateNameLower.includes(trimmedName)) {
            templateIdsFromFilter.push(parseInt(template[0]));
            break;
          }
        }
      }
      for (const trimmedName of filterTrimmedNames) {
        if (matchesMaterialCacheKeyFormat(trimmedName)) {
          materialSrcFromFilter.push(materialSrcFromCacheKey(trimmedName));
        }
      }
      list = list.filter(
        (materialSrc) =>
          templateIdsFromFilter.includes(materialSrc.templateId) ||
          materialSrcFromFilter.find((src) => materialSrcEquals(materialSrc, src)) !== undefined,
      );
    }
    setMaterialIdsToBake(list);
  }, [materialSrcList, filterList]);

  async function bakeMaterialsList(
    materialIds: MaterialSrc[],
    renderer: WebGLRenderer,
    refreshAll: boolean,
    addError: (error: BakingError) => void,
    addFinished: (materialId: MaterialSrc) => void,
  ) {
    let preexistingAwsPaths: string[] = [];
    if (!refreshAll) {
      preexistingAwsPaths = await listFiles(getMaterialBakingAwsRoot(cacheId));
    }

    const bakedColoredTemplateTextures = new Set<string>();
    let textureSharingCount = 0;
    let simpleColorCount = 0;
    let totalNewCount = 0;
    let preexistingCount = 0;
    for (const materialId of materialIds) {
      const materialParams = await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(materialId, false);
      if (!materialParams) {
        addError({ materialId, error: "Failed to fetch material parameters" });
        continue;
      }
      let preprocessedMaterialParams = preprocessMaterialParams(materialParams);
      if (designMaterialBakesToFlatColor(preprocessedMaterialParams)) {
        simpleColorCount++;
        addFinished(materialId);
        continue; // No need to cache flat color textures.
      }

      const isColoredTemplateTexture =
        materialId.layerConfigId === undefined &&
        designMaterialColorVariantsCanUseTemplateTexture(preprocessedMaterialParams);

      const awsPath = getMaterialBakingAwsPath(cacheId, materialId, isColoredTemplateTexture);

      if (isColoredTemplateTexture) {
        textureSharingCount++;
        if (bakedColoredTemplateTextures.has(awsPath)) {
          addFinished(materialId);
          continue;
        }
      }

      if (isColoredTemplateTexture) {
        preprocessedMaterialParams = {
          ...preprocessedMaterialParams,
          color: "#ffffff",
        };
        bakedColoredTemplateTextures.add(awsPath);
      }

      if (preexistingAwsPaths.includes(awsPath)) {
        ++preexistingCount;
        addFinished(materialId);
        continue;
      }

      ++totalNewCount;

      const bakedTexture = await designMaterialBakeColorMapToBlob({
        params: preprocessedMaterialParams,
        renderer,
        loadTextureUrl,
        hasSSFXRenderer: true,
        options: defaultDesignMaterialLoadOptions,
        createDesignMaterial: designMaterialNewFromPreprocessed,
      });
      //console.log(window.URL.createObjectURL(bakedTexture));
      //console.log(awsPath);
      try {
        const [, err] = await postFile(bakedTexture, awsPath, true);
        if (err) {
          const httpStatusCodeMessage = (err as FetchErrorResponse).statusCode
            ? ` (HTTP ${(err as FetchErrorResponse).statusCode})`
            : "";
          addError({
            materialId,
            error: "Failed to upload baked texture." + httpStatusCodeMessage + "\n" + err.message,
          });
        } else {
          addFinished(materialId);
        }
      } catch (e) {
        addError({
          materialId,
          error: "Failed to upload baked texture - exception:\n" + JSON.stringify(e, undefined, 2),
        });
      }
    }
    addLogMessage(
      `${textureSharingCount} materials used shared colored template textures. ${simpleColorCount} materials evaluated to a simple color. ${preexistingCount} textures already existed in the cache and were not refreshed. ${totalNewCount} textures were baked.`,
    );
  }

  async function bakeMaterials(materialIds: MaterialSrc[]) {
    setIsBaking(true);
    addLogMessage("Baking started");
    if (filterList.trim().length > 0) {
      addLogMessage(
        "Filtered list of material IDs to bake:\n" + materialIds.map((id) => logMaterialSrc(id)).join("\n"),
      );
      if (!refreshAllWhenBaking) {
        addErrorMessage("You have filtering on but not refreshing preexisting textures. This might be unintended.");
      }
    }
    const { renderer } = initSharedWebGL({
      reuse: WebGLContextReuse.AUTO,
      onScreenRenderingDisabled: true,
    });

    setFinishedImageCount(0);
    setCurrentBakeTotalCount(materialIds.length);

    let finishedCounter = 0;

    let retryIndex = 0;

    let materialIdsLeft = [...materialIds];

    while (retryIndex <= retries && finishedCounter < materialIds.length) {
      if (retryIndex > 0) {
        addLogMessage(`Retrying ${retryIndex} out of ${retries} retries`);
      }
      addLogMessage(`Baking ${materialIdsLeft.length} materials`);
      const erroredList: BakingError[] = [];

      function addError(error: BakingError) {
        erroredList.push(error);
        addErrorMessage(logMaterialSrc(error.materialId) + ": " + error.error);
      }

      function addFinished(_materialId: MaterialSrc) {
        ++finishedCounter;
        setFinishedImageCount(finishedCounter);
      }

      await bakeMaterialsList(materialIdsLeft, renderer, refreshAllWhenBaking, addError, addFinished);
      materialIdsLeft = erroredList.map((error) => error.materialId);
      ++retryIndex;
    }

    setErroredMaterialIds(materialIdsLeft);

    setIsBaking(false);
    addLogMessage("Baking finished");
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Cache Manager")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div className="ui" style={{ margin: "10px" }}>
        {texturesReady ? (
          <>
            <h2>Material Cache Manager</h2>
            <p>
              Normally the cache is specific to a given environment (dev / stage / prod). If you are running a local
              development server, then the local dev cache is used for baked textures by default. It&apos;s not shared
              with any other environment.
            </p>
            <Checkbox
              label="Use local dev cache."
              checked={useDevCache}
              onChange={(e) => setUseDevCache(e.target.checked)}
            />
            <p>File storage root: </p>
            <pre>{fileStorageBaseURL ? fileStorageBaseURL + getMaterialBakingAwsRoot(cacheId) : "Loading..."}</pre>
            <p>
              Retries on error:{" "}
              <input
                type="number"
                min={0}
                max={3}
                value={retries}
                onChange={(e) => setRetries(parseInt(e.target.value))}
              />
            </p>
            <Checkbox
              label="Refresh preexisting textures in the cache (very slow)."
              checked={refreshAllWhenBaking}
              onChange={(e) => setRefreshAllWhenBaking(e.target.checked)}
            />
            <div style={{ marginTop: "15px" }}>
              Materials to bake:
              <Select
                options={[
                  { label: "All (" + fullMaterialSrcList().length + ")", value: MaterialSrcList.All },
                  {
                    label: "Material Appearance Test Materials (" + materialAppearanceTestDefaultMaterials.length + ")",
                    value: MaterialSrcList.TestMaterials,
                  },
                ]}
                value={materialSrcList}
                onChange={(value) => setMaterialSrcList(value ? (value as MaterialSrcList) : MaterialSrcList.All)}
              />
            </div>
            <p>
              Filter by template name OR material cache key (one per line):
              <br />
              <textarea rows={10} cols={100} value={filterList} onChange={(e) => setFilterList(e.target.value)} />
            </p>
            <p>
              <input
                type="button"
                disabled={isBaking}
                onClick={() => {
                  void bakeMaterials(materialIdsToBake);
                }}
                value={"Bake material color textures (" + materialIdsToBake.length + ")"}
              />
            </p>
            <div>
              <progress value={finishedImageCount} max={currentBakeTotalCount} /> {finishedImageCount} /{" "}
              {currentBakeTotalCount}
            </div>
          </>
        ) : (
          <div>Loading textures...</div>
        )}
        <div>
          <h2>Console</h2>
          {erroredMaterialIds.length > 0 && (
            <input
              type="button"
              disabled={isBaking}
              onClick={() => void bakeMaterials(erroredMaterialIds)}
              value="Try baking again for materials that encountered errors"
            />
          )}
          <DebugLogViewer logMessages={logMessages} logElementRef={logElementRef} />
        </div>
      </div>
    </>
  );
}

function MaterialCacheManagerPag() {
  return <MaterialCacheManager />;
}

export const Route = createFileRoute("/debug/material-cache-manager")({
  component: withAuthentication(MaterialCacheManagerPag, { superAdmin: true }),
});
